/**
 * CommissionReceivablesPanel.jsx — TREATX MIXED SETTLEMENT (Super Admin)
 * ======================================================================
 *
 * Review queue for TreatX-originated bookings where the provider declared a
 * direct collection, so an ADDITIONAL TreatX commission is receivable.
 *
 * Every figure comes from the backend reconciliation
 * (`GET /api/finance/admin/commission-receivables`); nothing is recomputed
 * here. Approval calls the canonical issuance endpoint, which is idempotent
 * and fails closed when the TreatX commission VAT policy is unapproved.
 * Issuing a receivable never marks it paid.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, RefreshCw } from 'lucide-react';

import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';

const fmtMinor = (minor, currency) => {
  if (minor == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency: currency || 'EUR', maximumFractionDigits: 2,
    }).format(Number(minor) / 100);
  } catch {
    return `${(Number(minor) / 100).toFixed(2)} ${currency || ''}`;
  }
};

export const CommissionReceivablesPanel = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [result, setResult] = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api.get('/finance/admin/commission-receivables')
      .then((res) => setRows((res?.data || res)?.rows || []))
      .catch((err) => setError(err?.response?.data?.message || err?.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (row) => {
    setBusyId(row.financial_contract_id); setResult(null); setError(null);
    try {
      const res = await api.post(
        `/finance/admin/commission-receivables/${row.financial_contract_id}/approve`, {},
      );
      const data = res?.data || res;
      setResult({
        id: row.financial_contract_id,
        invoice: data?.invoice?.invoice_number,
        idempotent: data?.idempotent,
      });
      load();
    } catch (err) {
      const d = err?.response?.data?.detail || err?.response?.data || {};
      setError(d.message || err?.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="commission-receivables-panel">
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-[14px] font-semibold text-slate-800">
            {t('commissionReceivables.title')}
          </h3>
          <button type="button" onClick={load} className="text-slate-500 hover:text-slate-800"
                  data-testid="commission-receivables-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[12px] text-slate-500">{t('commissionReceivables.intro')}</p>

        {loading && <p className="text-[12px] text-slate-500 mt-3" data-testid="commission-receivables-loading">{t('common.loading')}</p>}
        {error && (
          <p className="text-[12px] text-rose-700 mt-3 inline-flex items-start gap-1"
             data-testid="commission-receivables-error">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{error}
          </p>
        )}
        {result && (
          <p className="text-[12px] text-emerald-800 mt-3" data-testid="commission-receivables-result">
            {result.idempotent
              ? t('commissionReceivables.alreadyIssued', { number: result.invoice })
              : t('commissionReceivables.issued', { number: result.invoice })}
          </p>
        )}

        {!loading && rows.length === 0 && !error && (
          <p className="text-[12px] text-slate-500 mt-3" data-testid="commission-receivables-empty">
            {t('commissionReceivables.empty')}
          </p>
        )}

        {rows.length > 0 && (
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-[12px]" data-testid="commission-receivables-table">
              <thead className="text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left px-2 py-1.5">{t('commissionReceivables.booking')}</th>
                  <th className="text-left px-2 py-1.5">{t('commissionReceivables.provider')}</th>
                  <th className="text-left px-2 py-1.5">{t('commissionReceivables.origin')}</th>
                  <th className="text-right px-2 py-1.5">{t('commissionReceivables.basis')}</th>
                  <th className="text-right px-2 py-1.5">{t('commissionReceivables.psp')}</th>
                  <th className="text-right px-2 py-1.5">{t('commissionReceivables.direct')}</th>
                  <th className="text-right px-2 py-1.5">{t('commissionReceivables.alreadyCharged')}</th>
                  <th className="text-right px-2 py-1.5">{t('commissionReceivables.remaining')}</th>
                  <th className="text-right px-2 py-1.5" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.financial_contract_id} className="border-b border-slate-100"
                      data-testid={`receivable-row-${r.financial_contract_id}`}>
                    <td className="px-2 py-1.5 font-mono text-[11px]">{r.financial_contract_id}</td>
                    <td className="px-2 py-1.5">{r.clinic_id}</td>
                    <td className="px-2 py-1.5">{t(`commissionReceivables.origins.${r.patient_origin}`, { defaultValue: r.patient_origin })}</td>
                    <td className="px-2 py-1.5 text-right">{fmtMinor(r.commissionable_gross_minor, r.currency)}</td>
                    <td className="px-2 py-1.5 text-right">{fmtMinor(r.psp_collected_minor, r.currency)}</td>
                    <td className="px-2 py-1.5 text-right">{fmtMinor(r.direct_collected_minor, r.currency)}</td>
                    <td className="px-2 py-1.5 text-right">
                      {fmtMinor(
                        (r.commission_covered_by_psp_minor || 0)
                        + (r.additional_commission_invoiced_minor || 0), r.currency,
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right font-semibold text-amber-800"
                        data-testid={`receivable-amount-${r.financial_contract_id}`}>
                      {fmtMinor(r.additional_commission_receivable_minor, r.currency)}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      {r.reconciliation_exception ? (
                        <span className="text-[11px] text-rose-700"
                              data-testid={`receivable-exception-${r.financial_contract_id}`}
                              title={r.reconciliation_exception.required_action}>
                          {t('commissionReceivables.exception')}
                        </span>
                      ) : (
                        <Button size="sm" disabled={busyId === r.financial_contract_id}
                                onClick={() => approve(r)}
                                data-testid={`receivable-approve-${r.financial_contract_id}`}>
                          {busyId === r.financial_contract_id
                            ? t('common.saving') : t('commissionReceivables.approve')}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-[11px] text-slate-400 mt-3" data-testid="commission-receivables-note">
          {t('commissionReceivables.vatNote')}
        </p>
      </div>
    </div>
  );
};

export default CommissionReceivablesPanel;
